import { API_APPRAISAL_V2 } from "../../../../config/Endpoints.js";
import { getApi, postApi, postFormDataMultipart, postRawJson, putApi } from "../../../utils/api.ts";
import { accessToken } from "../../../utils/auth.ts";
import type { ApiEnvelope, AppraisalDetail, AppraisalRow, SearchFilters } from "./types.ts";

function unwrap<T>(value: ApiEnvelope<T> | T): T {
  return value && typeof value === "object" && "data" in value ? (value as ApiEnvelope<T>).data : value as T;
}

export async function searchAppraisals(filters: SearchFilters) {
  const body = { employeeId: filters.employeeId ? Number(filters.employeeId) : null, status: filters.status || null, ...(filters.useHrStatus !== undefined ? { useHrStatus: filters.useHrStatus } : {}), letterType: filters.letterType || null, fromDate: filters.fromDate || null, toDate: filters.toDate || null, page: filters.page, size: filters.size };
  const { data } = await postApi<any>(`${API_APPRAISAL_V2}/search`, accessToken(), body);
  const payload = unwrap<any>(data) ?? {};
  const rows: AppraisalRow[] = payload.content ?? payload.items ?? payload.records ?? (Array.isArray(payload) ? payload : []);
  return { rows, totalElements: payload.totalElements ?? payload.total ?? rows.length, totalPages: payload.totalPages ?? Math.max(1, Math.ceil((payload.totalElements ?? rows.length) / filters.size)) };
}

export async function exportAppraisals(filters: Omit<SearchFilters, "page" | "size">) {
  const response = await fetch(`${API_APPRAISAL_V2}/export`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(accessToken() ? { Authorization: `Bearer ${accessToken()}` } : {}),
    },
    body: JSON.stringify({
      employeeId: filters.employeeId ? Number(filters.employeeId) : null,
      status: filters.status || null,
      letterType: filters.letterType || null,
      fromDate: filters.fromDate || null,
      toDate: filters.toDate || null,
    }),
  });
  if (!response.ok) {
    let message = "Unable to export appraisals.";
    try {
      const error = await response.json();
      message = error.message || error.error || message;
    } catch {
      // Keep the export-specific fallback for non-JSON error responses.
    }
    throw new Error(message);
  }
  const blob = await response.blob();
  const disposition = response.headers.get("content-disposition") ?? "";
  const encodedName = /filename\*=UTF-8''([^;]+)/i.exec(disposition)?.[1];
  const plainName = /filename="?([^";]+)"?/i.exec(disposition)?.[1];
  return {
    blob,
    filename: encodedName ? decodeURIComponent(encodedName) : plainName || "appraisal-approvals.xlsx",
  };
}

export async function bulkUpdateAppraisalStatus(appraisalIds: number[], status: string) {
  const { data } = await putApi<ApiEnvelope<unknown>>(
    `${API_APPRAISAL_V2}/bulk-status`,
    accessToken(),
    { appraisalIds, status },
  );
  return data;
}

export async function getAppraisal(id: number) {
  const { data } = await getApi<ApiEnvelope<AppraisalDetail> | AppraisalDetail>(`${API_APPRAISAL_V2}/${id}`, accessToken());
  const detail = unwrap(data);
  return { ...detail, id: detail.id ?? detail.appraisalId ?? id };
}

export async function decideAppraisal(id: number, action: "APPROVE" | "REJECT", cpoComments: string, annexure: Record<string, unknown> | null) {
  const { data } = await postApi<ApiEnvelope<unknown>>(`${API_APPRAISAL_V2}/${id}/decision`, accessToken(), { action, cpoComments, annexure });
  return data;
}

export async function updateAppraisal(id: number, payload: Record<string, unknown>) {
  const { data } = await putApi<ApiEnvelope<unknown>>(`${API_APPRAISAL_V2}/${id}`, accessToken(), payload);
  return data;
}

export async function submitAppraisal(id: number, comment: string) {
  const { data } = await postRawJson<ApiEnvelope<unknown>>(`${API_APPRAISAL_V2}/${id}/submit`, accessToken(), comment);
  return data;
}

export async function uploadAppraisalPdf(id: number, file: File) {
  const form = new FormData(); form.append("file", file);
  const { data } = await postFormDataMultipart<ApiEnvelope<unknown>>(`${API_APPRAISAL_V2}/${id}/pdf`, accessToken(), form);
  return data;
}

export async function scheduleAppraisal(id: number, scheduledAt: string, sendReminder: boolean) {
  const { data } = await postApi<ApiEnvelope<unknown>>(`${API_APPRAISAL_V2}/${id}/schedule`, accessToken(), { scheduledAt, sendReminder });
  return data;
}

export async function scheduleAppraisals(appraisalIds: number[], scheduledAt: string, sendReminder: boolean) {
  const { data } = await postApi<ApiEnvelope<unknown>>(`${API_APPRAISAL_V2}/schedule`, accessToken(), {
    appraisalIds,
    scheduledAt,
    sendReminder,
  });
  return data;
}

export function detailPayload(detail: AppraisalDetail): Record<string, unknown> {
  const baseValue = detail.baseValue ?? Math.round(
    (detail.compensation?.basic ?? 0) +
    (detail.compensation?.pfEmployeePart ?? 0) +
    (detail.compensation?.esic ?? 0) +
    (detail.allowances?.specialAllowance ?? 0) +
    (detail.allowances?.houseRentAllowance ?? 0) +
    (detail.allowances?.internetReimbursement ?? 0),
  );
  const annualRemarks = detail.annualComponent?.remarks?.trim() ?? "";
  const normalizedAnnualRemarks = annualRemarks.toLowerCase().replace(/[.*]+$/, "");
  const remarks = !annualRemarks ||
    normalizedAnnualRemarks === "annual fixed component shall be paid at the completion of 1 year" ||
    normalizedAnnualRemarks === "annual fixed component shall be paid on the completion of one year."
    ? null
    : annualRemarks;
  return {
    employeeId: detail.employeeId, letterType: detail.letterType, baseValue, letterIssueDate: detail.letterIssueDate,
    effectiveDate: detail.effectiveDate, salutation: detail.salutation, compensation: detail.compensation,
    allowances: detail.allowances, statutory: detail.statutory,
    annualComponent: detail.annualComponent ? { ...detail.annualComponent, remarks } : { enabled: false, amount: 0, remarks: null },
    noticePeriod: detail.noticePeriod, designationChange: detail.designationChange,
    designationChangeText: detail.designationChangeText,
  };
}
